import type { RouterContextType } from "@/api/permissions/permissions.api";
// SERV-1932: Upload Photos hidden on edit-visit for Bare Bones MVP (restore with the render below).
// import { UploadPhotos } from "@/components/features/WorkOrders/NewVisit/UploadPhotos";
import { VisitCreatedBy } from "@/components/features/WorkOrders/NewVisit/VisitCreatedBy";
import { VisitDetails } from "@/components/features/WorkOrders/NewVisit/VisitDetails";
import { Button } from "@/components/ui/Button/Button";
import { useEditVisitForm } from "@/hooks/work-order/useEditVisitForm";
import { VISIT_FIELD_ORDER } from "@/hooks/work-order/useVisitForm";
import { useVisitDetail } from "@/hooks/work-orders/visit/useVisitDetail";
import { authGuardWithPermissions } from "@/utils/authGuard";
import {
  leaveRecordTenant,
  RECORD_TENANT_SEARCH_FIELDS,
  syncRecordTenant,
} from "@/utils/recordTenantRoute";
import { scrollToFirstError } from "@/utils/scrollToFirstError";
import { showErrorAlert } from "@/utils/responseHandler";
import Loader from "@/components/ui/Loader/Loader";
import { Box, Flex, Stack, Text, Title } from "@mantine/core";
import { IconX } from "@tabler/icons-react";
import {
  createFileRoute,
  retainSearchParams,
  useNavigate,
  useRouter,
} from "@tanstack/react-router";
import { FormProvider } from "react-hook-form";
import type { FieldErrors } from "react-hook-form";
import { z } from "zod";
import styles from "@/routes/work-orders/$workOrderId/add-visit/add-visit.module.scss";

const editVisitSearchSchema = z.object({
  returnTo: z.enum(["clients", "locations"]).optional().catch(undefined),
  returnId: z.string().optional().catch(undefined),
  ...RECORD_TENANT_SEARCH_FIELDS,
});

export const Route = createFileRoute("/work-orders/$workOrderId/$visitId/edit/")({
  component: EditVisit,
  validateSearch: editVisitSearchSchema.parse,
  search: { middlewares: [retainSearchParams(["tenant"])] },
  onEnter: syncRecordTenant,
  onStay: syncRecordTenant,
  onLeave: leaveRecordTenant,
  beforeLoad: async (args) => {
    await authGuardWithPermissions({
      context: args.context as RouterContextType,
      location: args.location as { pathname: string },
    });
  },
});

function EditVisit() {
  const { workOrderId, visitId } = Route.useParams();
  const { returnTo, returnId } = Route.useSearch();
  const navigate = useNavigate();
  const router = useRouter();
  const { data: visitData, isLoading, error } = useVisitDetail(visitId);

  const goBack = () => {
    if (returnTo === "locations" && returnId) {
      void navigate({
        to: "/locations",
        search: { quickViewLocationId: returnId },
        replace: true,
      });
      return;
    }
    if (returnTo === "clients" && returnId) {
      void navigate({
        to: "/clients",
        search: { quickViewClientId: returnId },
        replace: true,
      });
      return;
    }
    if (router.history.length > 1) {
      router.history.back();
    } else {
      void navigate({ to: "/work-orders/$workOrderId/$visitId", params: { workOrderId, visitId } });
    }
  };

  const handleClose = goBack;

  const handleViewDetails = () => {
    navigate({
      to: "/work-orders/$workOrderId/$visitId",
      params: { workOrderId, visitId },
    });
  };

  if (isLoading) {
    return <Loader />;
  }

  if (error || !visitData) {
    return (
      <Flex align="center" justify="center" h="70vh">
        <Text c="red">Failed to load visit details. Please try again.</Text>
      </Flex>
    );
  }

  return (
    <EditVisitForm
      visitId={visitId}
      workOrderId={workOrderId}
      visitData={visitData}
      onClose={handleClose}
      onViewDetails={handleViewDetails}
      onNavigateBack={goBack}
    />
  );
}

interface EditVisitFormProps {
  visitId: string;
  workOrderId: string;
  visitData: NonNullable<ReturnType<typeof useVisitDetail>["data"]>;
  onClose: () => void;
  onViewDetails: () => void;
  onNavigateBack: () => void;
}

function EditVisitForm({
  visitId,
  workOrderId,
  visitData,
  onClose,
  onViewDetails,
  onNavigateBack,
}: EditVisitFormProps) {
  const { methods, onSubmit, isUpdateLoading } = useEditVisitForm({
    visitId,
    workOrderId,
    onNavigateBack,
    initialValues: {
      visitTypeId: String(visitData.visitTypeId),
      tradeId: visitData.trades[0]?.workOrderTradeId ?? "",
      subtradeId: visitData.trades[0]?.subtrades[0]?.workOrderTradeId ?? null,
      // SERV-1932: trades are backend-owned; edit no longer sends or derives them. Kept for restore.
      // existingTradeIds: visitData.trades.flatMap((t) => [
      //   t.workOrderTradeId,
      //   ...t.subtrades.map((s) => s.workOrderTradeId),
      // ]),
      workDescription: visitData.visitDescription,
      existingDocuments: visitData.documents ?? [],
      hasRelatedWorkOrder: visitData.hasRelatedWorkOrder ?? false,
      relatedWorkOrderId: visitData.relatedWorkOrderId ?? null,
      lockSchedule: visitData.lockSchedule,
      lockScheduleConstraintReasonId: visitData.lockScheduleConstraintReasonId,
    },
  });

  const onInvalid = (errors: FieldErrors) => {
    showErrorAlert("Please fill in all required fields.", "Validation Error");
    scrollToFirstError(errors, methods.setFocus, VISIT_FIELD_ORDER);
  };

  return (
    <FormProvider {...methods}>
      <Box className={styles.section}>
        <Flex justify="space-between" align="center" className={styles.header}>
          <Title order={3} className={styles.title}>
            Edit Visit Details
          </Title>
          <IconX size={16} className={styles.closeIcon} onClick={onClose} aria-label="Close" />
        </Flex>
        <Box className={styles.container}>
          <Flex gap="xl" style={{ width: "100%" }}>
            <Stack gap="lg" style={{ flex: 1 }}>
              <VisitDetails
                workOrderId={workOrderId}
                visitNumber={visitData.visitNumber - 1}
                visitTypeLabel={visitData.visitType}
                tradeLabel={visitData.trades[0]?.tradeName}
                subtradeLabel={visitData.trades[0]?.subtrades[0]?.subtradeName}
              />
              {/* SERV-1932: Hide Upload Photos on edit-visit for Bare Bones MVP. */}
              {/* <UploadPhotos /> */}
            </Stack>
            <Stack gap="lg" style={{ flex: 0.5 }}>
              <VisitCreatedBy />
            </Stack>
          </Flex>
        </Box>
        <Box className={styles.footer}>
          <Box className={styles.footerContent}>
            <Button variant="secondary" onClick={onViewDetails} mr="md">
              View Visit Details
            </Button>
            <Button
              variant="primary"
              onClick={methods.handleSubmit(onSubmit, onInvalid)}
              loading={methods.formState.isSubmitting || isUpdateLoading}
              disabled={methods.formState.isSubmitting || isUpdateLoading}
            >
              Save Changes
            </Button>
          </Box>
        </Box>
      </Box>
    </FormProvider>
  );
}
